





































































































































































import {
  Component,
  Prop,
  Vue, Watch,
} from 'vue-property-decorator';
import Dialog from '../components/Dialog.vue';
import {Job} from '@/types';
import JobsService from '@/services/jobs.service';
import { Getter } from 'vuex-class';
import { copyToClipboard } from '@/classes/utils';
import GenericButton from '@/components/GenericButton.vue';

interface LogLine {
  type: 'error' | 'warning';
  message: string;
}

@Component({
  components: {GenericButton, Dialog},
})
export default class JobHistory extends Vue {
  @Prop() private showDialog!: boolean;
  @Prop({ default: null }) private focusJobId!: string | null;
  @Getter private projectById!: any;
  @Getter private activeAccountId!: any;
  @Getter private hasPermission: any;

  private copyToClipboard: (text: string) => boolean = copyToClipboard;
  private loadingJobs: boolean = false;
  private jobs: Job[] = [];
  private searchQuery: string = '';
  private selectedJobId: string | null = null;
  private revealCount: number = 50;
  private readonly revealBatchSize: number = 50;

  private async mounted() {
    await this.loadJobHistory();
  }

  @Watch('showDialog')
  private async onShowDialogChange(isOpen: boolean) {
    if (isOpen) {
      await this.loadJobHistory();
    }
  }

  @Watch('searchQuery')
  private onSearchChange() {
    this.revealCount = this.revealBatchSize;
  }

  private get sortedJobs(): any[] {
    return [...this.jobs].sort((a: any, b: any) => -a.created_at.localeCompare(b.created_at));
  }

  private get filteredJobs(): any[] {
    const q = (this.searchQuery || '').trim().toLowerCase();
    if (!q) {
      return this.sortedJobs;
    }
    return this.sortedJobs.filter((job: any) => {
      const haystack = [
        this.getType(job),
        job.status,
        job.data && job.data.filename,
        job.data && job.data.errors ? job.data.errors.join(' ') : '',
        job.data && job.data.warnings ? job.data.warnings.join(' ') : '',
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }

  private get visibleJobs(): any[] {
    return this.filteredJobs.slice(0, this.revealCount);
  }

  private get selectedJob(): any {
    return this.jobs.find((job: any) => job.id === this.selectedJobId) || null;
  }

  private async loadJobHistory() {
    this.loadingJobs = true;
    this.jobs = [];
    this.searchQuery = '';
    this.revealCount = this.revealBatchSize;
    try {
      const jobsPromise = JobsService.getJobs();
      const promises: Array<Promise<Job[]>> = [jobsPromise];

      if (!!this.activeAccountId) {
        promises.push(JobsService.getAccountJobs(this.activeAccountId));
      }
      if (this.hasPermission('administration:view')) {
        promises.push(JobsService.getAdminJobs());
      }

      const results = await Promise.all(promises);
      const combinedJobs = results.flat();

      Vue.set(this, 'jobs', combinedJobs);
      this.selectFocusedOrDefaultJob();
    } finally {
      this.loadingJobs = false;
    }
  }

  private selectFocusedOrDefaultJob() {
    const jobs = this.sortedJobs;
    if (!jobs.length) {
      this.selectedJobId = null;
      return;
    }

    if (this.focusJobId) {
      const index = jobs.findIndex((job: any) => job.id === this.focusJobId);
      if (index !== -1) {
        this.revealCount = Math.max(this.revealCount, index + 10);
        this.selectedJobId = this.focusJobId;
        this.$nextTick(() => this.scrollSelectedIntoView());
        return;
      }
    }

    this.selectedJobId = jobs[0].id;
  }

  private scrollSelectedIntoView() {
    if (!this.selectedJobId) { return; }
    const refKey = 'job-' + this.selectedJobId;
    const ref = this.$refs[refKey] as Vue | Vue[] | undefined;
    const component = Array.isArray(ref) ? ref[0] : ref;
    const el = component && (component as Vue).$el;
    if (el) {
      el.scrollIntoView({ block: 'center' });
    }
  }

  private selectJob(job: any) {
    this.selectedJobId = job.id;
  }

  private getType(item: any) {
    switch (item.type) {
      case 'location_import': return 'Import: Location';
      case 'device_import': return 'Import: Device';
      case 'location_export': return 'Export: Locations';
      case 'device_export': return 'Export: Devices';
      case 'data_export': return 'Export: Data';
      case 'admin_subscription_export': return 'Export: Admin subscriptions';
      case 'subscription_export':
        return item.account_id ? 'Export: Account subscriptions' : 'Export: Subscriptions';
      case 'devices_assign': return 'Assign: Devices';
      case 'devices_unassign': return 'Unassign: Devices';
      case 'devices_test': return 'Test Bench: Devices';
      case 'devices_unmounttest': return 'Test Bench Unmount: Devices';
      case 'devices_dispose': return 'Dispose: Devices';
      case 'devices_decommission': return 'Decommission: Devices';
      case 'devices_stock': return 'Stock: Devices';
      case 'devices_unmount': return 'Unmount: Devices';
      case 'device_subscribe': return 'Subscribe: Devices';
      case 'gateways_subscribe': return 'Subscribe: Gateways';
      case 'configuration_profile_assign': return 'Assign: Configuration profile';
      case 'devices_transfer': return 'Transfer: Devices';
      case 'subscriptions_transfer': return 'Transfer: Subscriptions';
      case 'account_audit_export': return 'Export: Account user activity';
      default: return 'Import';
    }
  }

  private getStatusLabel(item: any): string {
    return (item.status === 'completed' || item.status === 'completed with warnings')
    && item.data && item.data.errors && item.data.errors.length
        ? 'Partially completed'
        : item.status;
  }

  private getStatusColor(status: string) {
    switch (status) {
      case 'completed': return 'green--text';
      case 'completed with warnings': return 'amber--text';
      case 'running': return 'primary--text';
      case 'failed': return 'red--text';
      default: return 'default--text';
    }
  }

  private getStatusIcon(job: any): string {
    const hasErrors = !!(job.data && job.data.errors && job.data.errors.length);
    const hasWarnings = !!(job.data && job.data.warnings && job.data.warnings.length);
    if (job.status === 'failed') { return 'mdi-alert-circle'; }
    if (job.status === 'running') { return 'mdi-progress-clock'; }
    if (hasErrors || hasWarnings) { return 'mdi-alert'; }
    return 'mdi-check-circle';
  }

  private getStatusIconColor(job: any): string {
    const hasErrors = !!(job.data && job.data.errors && job.data.errors.length);
    const hasWarnings = !!(job.data && job.data.warnings && job.data.warnings.length);
    if (job.status === 'failed') { return 'red'; }
    if (job.status === 'running') { return 'primary'; }
    if (hasErrors || hasWarnings) { return 'amber darken-2'; }
    return 'green';
  }

  private projectName(job: any): string {
    if (!job.data || !job.data.project_id) { return ''; }
    return (this.projectById(job.data.project_id) || {}).name || '';
  }

  private filenameOrDownload(job: any): { text: string; href: string; label: string } {
    if (job.type && job.type.match(/_export/) && job.data && job.data.url) {
      return { text: 'Download', href: job.data.url, label: 'Exported file' };
    }
    if (job.data && job.data.filename) {
      return { text: job.data.filename, href: '', label: 'Imported file' };
    }
    return { text: '', href: '', label: '' };
  }

  private hasAnyStat(job: any): boolean {
    return !!(job.data && (
        job.data.rows !== undefined
        || job.data.imported !== undefined
        || job.data.processed !== undefined
        || job.data.skipped !== undefined
    ));
  }

  private logLines(job: any): LogLine[] {
    if (!job || !job.data) { return []; }
    const errors = (job.data.errors || []).map((message: string) => ({ type: 'error' as const, message }));
    const warnings = (job.data.warnings || []).map((message: string) => ({ type: 'warning' as const, message }));
    return [...errors, ...warnings];
  }

  private logSummary(job: any): string {
    const errorCount = (job.data && job.data.errors && job.data.errors.length) || 0;
    const warningCount = (job.data && job.data.warnings && job.data.warnings.length) || 0;
    const parts: string[] = [];
    if (errorCount) {
      parts.push(`${errorCount} ${errorCount === 1 ? 'error' : 'errors'}`);
    }
    if (warningCount) {
      parts.push(`${warningCount} ${warningCount === 1 ? 'warning' : 'warnings'}`);
    }
    return parts.join(', ');
  }

  private copyLog(job: any) {
    const lines = this.logLines(job).map((l) => `[${l.type === 'error' ? 'ERROR' : 'WARN'}] ${l.message}`);
    this.copyToClipboard(lines.join('\n'));
  }
}
